import React from "react";

function ImagePreview({ src, alt, previewWidth = "15cm" }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <img
        src={src}
        alt={alt}
        style={{
          display: "block",
          width: previewWidth,
          height: "auto",
          marginTop: 10,
          borderRadius: 8,
          border: "1px solid #e5e5e5",
          boxShadow: "0 1px 6px #0001",
          cursor: "zoom-in",
          userSelect: "none",
        }}
        onClick={() => setOpen(true)}
      />
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.75)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            cursor: "zoom-out",
          }}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={src}
            alt={alt}
            style={{
              maxWidth: "95vw",
              maxHeight: "95vh",
              borderRadius: 10,
              background: "#fff",
              boxShadow: "0 10px 30px rgba(0,0,0,.35)",
            }}
          />
        </div>
      )}
    </>
  );
}

export default function StatsOverviewPageEn() {
  return (
    <div
      style={{
        maxWidth: 900,
        margin: "48px auto",
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 2px 12px #0001",
        padding: 36,
        fontFamily: "inherit",
        color: "#222",
      }}
    >
        <h2 id="stats-hero" style={{ fontSize: 42, fontWeight: 900, color: "#006b44", margin: 0 }}>
        Analytics services for online radio
      </h2>

      <p style={{ margin: "16px 0 10px", fontSize: 18 }}>
        <strong>Radiomonitor.net</strong> is not just data collection — it’s in-depth analysis. You see
        how your audience changes in real time, track dynamics by day, week,
        month, and even by year, and analyze prime time and content effectiveness.
      </p>
      <p style={{ margin: "0 0 24px", fontSize: 18 }}>
        Below is a breakdown of the services that make it possible:
      </p>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Live listeners</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>What is happening right now?</b><br />
          Shows the number of listeners in real time: right now, from which devices and which cities.
        </p>
        <ImagePreview src="/assets/stats/LiveService(en).png" alt="Live listeners" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Listeners</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Who listens to you?</b><br />
          Counts all connections and unique listeners — the core audience metric that all other analyses rely on.
        </p>
        <ImagePreview src="/assets/stats/ListenersService(en).png" alt="Listeners" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>
          Devices & OS
          <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: '#4a4a4a' }}>
            Audience Devices & Operating Systems
          </span>
        </h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          This service shows which devices and operating systems your station is listened from on the selected date (session-based).
          It aggregates listening sessions and builds a clear technical audience profile so you can see where your listener “lives” and how to optimize the player, site, app, and creatives.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>The report includes:</b>
        </p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>Device type (e.g., Mobile / Desktop / Other)</li>
          <li>Operating system (Android, iOS, Windows, macOS, etc.)</li>
          <li>Device family (top categories)</li>
          <li>Clients (top client apps/players)</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Why it matters:</b>
        </p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>Product optimization: know what dominates (Android vs iOS, mobile vs desktop) and prioritize UI/UX, testing, and support accordingly.</li>
          <li>Stream and player quality: quickly detect anomalies for specific OS/clients (e.g., if a certain player causes many drops or weird sessions).</li>
          <li>Growth planning: make evidence-based decisions on whether you need a dedicated app/update and which platforms to support first.</li>
          <li>Advertising & partnerships: match formats and creatives to the real technical audience and back it up for advertisers.</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Analysis period:</b> selected date (optional country filter available).
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Methodology:</b> aggregate listening sessions for the chosen date → group by device type / OS / device family / client → count volume and share (%) of the total sessions.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <ImagePreview src="/assets/stats/Devices&OS01_en.png" alt="Devices & OS — overview" />
          <ImagePreview src="/assets/stats/Devices&OS02_en.png" alt="Devices & OS — client breakdown" />
        </div>
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Audience geography</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Where do they listen?</b><br />
          Maps your audience — countries, cities, share of Ukrainian audience. A clear picture of regional reach.
        </p>
        <ImagePreview src="/assets/stats/Geo(en).png" alt="Audience geography" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Peak load</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>When do they listen the most?</b><br />
          Detects peak hours and your prime time — perfect for planning ad breaks.
        </p>
        <ImagePreview src="/assets/stats/PeakService(en).png" alt="Peaks" />
      </section>

      <section style={{ marginBottom: 18 }}>
          <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>
            Day-of-Week Listening Profile (AHA)
            <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: '#4a4a4a' }}>
              Average Hourly Audience by Day of Week
            </span>
          </h3>
          <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
            This indicator shows how the station’s average audience changes throughout the day depending on the weekday.
            For each day (Monday, Tuesday, … Sunday) the system calculates the hourly average of listeners,
            averaging all like-named days in the selected period — for example, all Mondays of a month or all Sundays over the last three months.
          </p>
          <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
            <li>see characteristic differences between weekdays and weekends;</li>
            <li>identify the hours with the highest audience activity each day;</li>
            <li>verify audience stability across the week;</li>
            <li>confirm or adjust prime-time slots.</li>
          </ul>
          <p style={{ margin: "6px 0", lineHeight: 1.5 }}>
            Analysis periods: 1, 3, 6, 9 or 12 months.<br />
            Calculation method: average per minute points → average per hour → average of identical slots (weekday, hour).
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
            <ImagePreview src="/assets/stats/AHA (en)1.png" alt="Day-of-Week Listening Profile — trend" />
            <ImagePreview src="/assets/stats/AHA (en)2.png" alt="Day-of-Week Listening Profile — heatmap" />
          </div>
        </section>

      <section style={{ marginBottom: 18 }}>
          <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>TSL</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>How long do they listen?</b><br />
          Calculates average listening time (TSL). Reflects audience engagement and content quality.
        </p>
        <ImagePreview src="/assets/stats/TslService(en).png" alt="TSL" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>TLH</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          TLH (Total Listening Hours) shows how many hours in total your station was listened to during the selected period.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>What does TLH measure?</b><br />
          It sums the duration of every listening session and converts it into hours:
        </p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>TLH per day — the listener-hours recorded on a given date.</li>
          <li>TLH for a period (total) — the number of listening hours accumulated over the selected range.</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Put simply, TLH answers the question: “How much time did the audience spend with our station?”
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Why it matters</b><br />
          TLH is one of the clearest currencies for valuing the audience and monetizing it:
        </p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>
            Ad sales — TLH is easy to show to advertisers as the volume of contact with your content (“this many listener-hours”).
          </li>
          <li>Period comparison — quickly see whether the total listening volume grows week-over-week or month-over-month.</li>
          <li>
            Measuring the effect of changes/promos — after campaigns, format tweaks or host changes TLH shows if the overall “attention time” increased.
          </li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>How to read the TLH report</b>
        </p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>Daily chart — how many listening hours each day contributed.</li>
          <li>TLH (total) — the sum of hours for the whole period.</li>
          <li>Session count — how many connections happened (shows what drives TLH up or down).</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          TLH pairs nicely with metrics like TSL, but on its own it still gives a commercially intuitive number — the total hours of attention your station received.
        </p>
        <ImagePreview src="/assets/stats/tlh_en.png" alt="TLH" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>New listeners</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>How many newcomers?</b><br />
          Counts new listeners by day, week, or month to show your audience growth.
        </p>
        <ImagePreview src="/assets/stats/NewListenersService(en).png" alt="New listeners" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>ADR (Average Daily Reach)</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Average Daily Reach of the station.</b>
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          This metric shows how many <b>unique listeners</b> on average tune in every day during the selected period.
          For each date the system counts distinct listeners and then averages the results across the range.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55, fontWeight: 600 }}>ADR helps you:</p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>evaluate the <b>real daily audience size</b> of the station;</li>
          <li>see how consistently the station attracts listeners in the chosen window;</li>
          <li>back up sales decks with a <b>clear reach number</b> for the period.</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Analysis period:</b> any selected date range within the available listening history.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}><b>Calculation method:</b></p>
        <ol style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>For each day determine the <b>number of unique listeners per day</b>.</li>
          <li>Average the daily values to obtain the Average Daily Reach for the entire period.</li>
        </ol>
        <ImagePreview src="/assets/stats/ADR (en).png" alt="ADR — example" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>AQH (Average Quarter-Hour Audience)</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Average number of simultaneous listeners.</b>
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Shows <b>how many listeners simultaneously</b> listen to the station on average within the selected period.
          All listening sessions are aggregated into time slots (e.g., by hours) and the average audience is shown for every slot.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55, fontWeight: 600 }}>AQH lets you:</p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>see how audience size changes throughout the day;</li>
          <li>identify slots with the highest and lowest listener counts;</li>
          <li>plan ad breaks in the most profitable slots;</li>
          <li>evaluate how schedule changes affect the real audience.</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Analysis period:</b> any date range within the available listening history.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}><b>Calculation method:</b></p>
        <ol style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>For every minute the number of active sessions is recorded.</li>
          <li>For each time slot (e.g., hour) the <b>minute-level average</b> is calculated.</li>
          <li>Slot values build the AQH profile for the selected period.</li>
        </ol>
        <ImagePreview src="/assets/stats/AQH (en)01.png" alt="AQH — chart" />
        <ImagePreview src="/assets/stats/AQH (en)02.png" alt="AQH — heatmap" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>
          Reach (Period Reach)
        </h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Reach shows <b>how many unique listeners</b> tuned in at least once during the selected period.
          Every repeated visit by the same unique listener — on the same day, week or month — is counted only once.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55, fontWeight: 600 }}>Reach helps you:</p>
        <ul style={{ margin: "6px 0 6px 22px", lineHeight: 1.5 }}>
          <li>see how many unique listeners turned on the station at least once over a day, week, month or any custom range;</li>
          <li>show advertisers how many different people could actually hear their ads in that period;</li>
          <li>explore the geography of these unique listeners (top countries plus the “no geography” share).</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55, fontWeight: 600 }}>How it’s calculated:</p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          For the chosen period we gather everyone who listened at least once and count each listener only once.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          If someone listened 10 times in a week, Reach still treats it as 1 listener for that week, not 10.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Example: a person listens on Monday, Wednesday and Friday — in weekly Reach that’s still one listener for the week.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          That’s why Reach reflects not the number of sessions but the number of different people who tuned in at least once during the period.
        </p>
        <ImagePreview src="/assets/stats/Reach_en.png" alt="Reach — example" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Retention</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Do listeners return?</b><br />
          Measures how many new listeners come back the next day or within a week — the key to the first-impression quality.
        </p>
        <ImagePreview src="/assets/stats/RetentionService(en).png" alt="Retention" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Loyalty index</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Who is your core?</b><br />
          Highlights regular, loyal listeners. Shows brand strength and trust in your content.
        </p>
        <ImagePreview src="/assets/stats/LoyaltyService(en).png" alt="Loyalty index" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>TOP</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>What’s most popular?</b><br />
          Builds rankings: top cities, platforms, devices and — if available — music and shows. Ready-made stats for presentations.
          <br />
          Requires importing the station’s schedule.
        </p>
        <ImagePreview src="/assets/stats/Top40Service(en).png" alt="TOP" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>LTR (Listen-Through Rate)</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Ad completion for audio spots.</b><br />
          LTR shows what share of listeners stay until the end of each commercial break. It turns the question 'the ad aired' into a
          clearer KPI: <b>'How many people actually heard the ad to the last second?'</b>
        </p>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>What LTR measures</h4>
        <ul style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>
            <b>Impressions</b> — how many unique listeners were connected at the start of the block;
          </li>
          <li>
            <b>Completions</b> — how many of them stayed until the end;
          </li>
          <li>
            <b>LTR, %</b> — the share that listened through (Completions/Impressions × 100).
          </li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          The dashboard lists every break within the selected day: start/stop time, creative lineup, audience size and completion rate.
        </p>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>How it works</h4>
        <ol style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>The service ingests the actual on-air log and automatically identifies ad breaks.</li>
          <li>For each break it tracks who was listening at the start (impressions) and who stayed until the end (completions).</li>
          <li>Based on that, the system calculates LTR in percent for every block separately.</li>
        </ol>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>Why use it</h4>
        <ul style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>Evaluate audio-ad effectiveness and spot creative or hour-by-hour drop-offs.</li>
          <li>Compare creatives and placements by how well they retain the audience.</li>
          <li>Optimize scheduling by seeing which dayparts work better for ads.</li>
          <li>Provide sales arguments with an actual listen-through percentage instead of abstract impressions.</li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          LTR lives in the Statistics area as a dedicated tool for analyzing ad breaks.
        </p>
        <ImagePreview src="/assets/stats/lpt_en.png" alt="LTR interface" />
      </section>

      <section style={{ marginBottom: 18 }}>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>
          Quartiles LTR (Listen-Through Rate by Quartiles)
        </h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>Ad listen-through by quartiles.</b>
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Quartiles LTR shows how many real listeners stayed with a commercial until 25%, 50%, 75%, and 100% of its duration. For every airing
          the system records Impressions (unique listeners present at the start) and how many people reached each quartile.
        </p>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Classic LTR gives one final data point — listened or not. Quartiles LTR pinpoints the exact segment where the audience begins to drop off.
        </p>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>What Quartiles LTR measures</h4>
        <ul style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>
            <b>Impressions</b> — how many unique listeners are connected when the creative starts.
          </li>
          <li>
            <b>Q25/Q50/Q75/Q100</b> — how many of them stay until 25%, 50%, 75%, and 100% of the duration.
          </li>
          <li>
            A daily row for each airing: start/stop time, creative name, audience at every quartile.
          </li>
        </ul>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>Additional slices</h4>
        <ul style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>
            <b>GEO by creative</b> — how many listeners of this airing came from each country.
          </li>
          <li>
            <b>IP-level detail</b> — a technical list of listener IPs for a specific creative and country.
          </li>
        </ul>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>How it works</h4>
        <ol style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>The service ingests the actual broadcast log and detects every commercial airing per day.</li>
          <li>For each airing it identifies all listeners who were on air at the start (impressions).</li>
          <li>Session data shows how much of the creative each listener really heard (with a ±5 second allowance, same as classic LTR).</li>
          <li>Each listener is assigned to the highest quartile reached (0/25/50/75/100%), then the system counts audience per level.</li>
          <li>In parallel it builds GEO slices per creative (country → listeners) and an IP list.</li>
        </ol>
        <h4 style={{ margin: "12px 0 6px", fontSize: 18, fontWeight: 700 }}>Why use it</h4>
        <ul style={{ margin: "0 0 12px 22px", lineHeight: 1.55 }}>
          <li>Get a true measure of audio-ad effectiveness instead of just “the spot aired”.</li>
          <li>Compare creatives or placements by real listening depth.</li>
          <li>Evaluate the role’s position inside the break — first, middle, last — and see where the audience holds better.</li>
          <li>Analyze GEO response: how different countries react to the same creative and whether it hits the right markets.</li>
          <li>Run deep technical audits thanks to IP-level data when needed.</li>
          <li>
            Enable “pay per listened spot” models — count only the impressions where a listener reached 50–75% within the required country.
          </li>
        </ul>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          Quartiles LTR lives in the Statistics section as a separate tool with quartile, GEO, and IP detail for every advertising creative.
        </p>
        <ImagePreview src="/assets/stats/quartiles_ltr_en01.png" alt="Quartiles LTR — quartile table" />
        <ImagePreview src="/assets/stats/quartiles_ltr_en02.png" alt="Quartiles LTR — GEO modal" />
        <ImagePreview src="/assets/stats/quartiles_ltr_en03.png" alt="Quartiles LTR — IP detail" />
      </section>

      <section>
        <h3 style={{ fontWeight: 800, fontSize: 22, margin: "12px 0 6px" }}>Advertising statistics</h3>
        <p style={{ margin: "6px 0", lineHeight: 1.55 }}>
          <b>How effective are your ads?</b><br />
          Counts impressions, reach, frequency and CPM — clear and transparent stats for advertisers.
          <br />
          Requires importing the station’s schedule.
        </p>
        <ImagePreview src="/assets/stats/AdsService(en).png" alt="Advertising statistics" />
      </section>
    </div>
  );
}
